import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  FacebookShareButton,
  XShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  TumblrShareButton,
  RedditShareButton,
} from 'react-share';
import { Tooltip, Window, WindowHeader } from '../overlays';
import {
  ShareVideoWindowState,
  WINDOW_HEIGHT,
  WINDOW_WIDTH,
} from './shareVideoWindowState';
import { Button, TextInputControlled } from '../input';
import {
  FacebookSquareSVGIcon,
  LinkedInSquareSVGIcon,
  RedditSquareSVGIcon,
  TumblrSquareSVGIcon,
  TwitterSquareSVGIcon,
  WhatsappSquareSVGIcon,
  CopyLinkSVGIcon,
  CodeSVGIcon,
} from '..';
import { SocialSharePlatforms } from '../../services/analytics/stream';

type Props = {
  model: ShareVideoWindowState;
  visible?: boolean;
};

export const ShareVideoWindow = observer((props: Props) => {
  const { model } = props;
  const { modalTitle } = model;

  return (
    <Window
      model={model.window}
      visible={props.visible}
      className="share-video-window"
    >
      <WindowHeader model={model.window} title={modalTitle} />

      <hr />
      <div className="window-content">
        <p className="p-bold">Share on Social</p>
        <SocialShare model={model} />
      </div>
    </Window>
  );
});

const SocialShare = observer(({ model }: { model: ShareVideoWindowState }) => {
  return (
    <>
      <div className="share-message">
        <TextInputControlled
          model={model.messageInput}
          feedbackPosition="topEnd"
          placeholder="Type in message"
          label="Message"
          multiline
        />
      </div>
      <div className="share-label">Share on</div>
      <div className="share-buttons">
        <FacebookShareButton
          className="btn btn-core medium"
          resetButtonStyle={false}
          url={model.sharedLink!}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Facebook)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <FacebookSquareSVGIcon />
        </FacebookShareButton>
        <XShareButton
          className="btn btn-core medium"
          resetButtonStyle={false}
          url={model.sharedLink!}
          text={model.titleWithMessage}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Twitter)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <TwitterSquareSVGIcon />
        </XShareButton>
        <LinkedinShareButton
          className="btn btn-core medium"
          resetButtonStyle={false}
          url={model.sharedLink!}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Linkedin)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <LinkedInSquareSVGIcon />
        </LinkedinShareButton>
        <WhatsappShareButton
          url={model.sharedLink!}
          className="btn btn-core medium"
          resetButtonStyle={false}
          message={model.titleWithMessage}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Whatsapp)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <WhatsappSquareSVGIcon />
        </WhatsappShareButton>
        <TumblrShareButton
          className="btn btn-core medium"
          resetButtonStyle={false}
          url={model.sharedLink!}
          postTitle={model.title}
          caption={model.message}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Tumblr)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <TumblrSquareSVGIcon />
        </TumblrShareButton>
        <RedditShareButton
          className="btn btn-core medium"
          resetButtonStyle={false}
          url={model.sharedLink!}
          shareTitle={model.titleWithMessage}
          onShareWindowClose={() =>
            model.handleShareDialogClose(SocialSharePlatforms.Reddit)
          }
          windowHeight={WINDOW_HEIGHT}
          windowWidth={WINDOW_WIDTH}>
          <RedditSquareSVGIcon />
        </RedditShareButton>

        <div className="divider"></div>

        <Tooltip content="Copy Link">
          <Button
            className="copy-link-btn"
            icon={<CopyLinkSVGIcon />}
            onClick={() => model.copyLink()}
          />
        </Tooltip>

        <Tooltip content="Copy Embed Code">
          <Button
            className="copy-link-btn"
            icon={<CodeSVGIcon />}
            onClick={() => model.copyIFrameEmbedCode()}
          />
        </Tooltip>
      </div>
    </>
  );
});
